import { LanguageSelect, t } from '../i18n';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/axios';

const ADMIN_LOGO_PATH = '/assets/night-poker-business-logo.png';
const CONFIGURED_SIGNUP_ENDPOINT = (process.env.REACT_APP_ADMIN_SIGNUP_ENDPOINT || '').trim();
const DEFAULT_SIGNUP_ENDPOINTS = ['/admin/auth/register', '/admin/auth/signup'];
const MARKETING_CONSENT_VERSION = (
  process.env.REACT_APP_MARKETING_CONSENT_VERSION || '2026-07-25-v1'
).trim();

function BrandLogo() {
  const [failed, setFailed] = useState(false);

  return (
    <span className="signup-brand-logo">
      {!failed ? (
        <img
          src={ADMIN_LOGO_PATH}
          alt="NIGHT Poker Business"
          onError={() => setFailed(true)}
        />
      ) : (
        <span>NIGHT Poker Business</span>
      )}
    </span>
  );
}

function getErrorMessage(error) {
  const data = error?.response?.data;
  const rawCode = String(data?.code || data?.error || error?.code || '').trim().toUpperCase();
  const knownMessages = {
    ID_ALREADY_EXISTS: t('이미 사용 중인 아이디입니다.'),
    USER_ALREADY_EXISTS: t('이미 사용 중인 아이디입니다.'),
    EMAIL_ALREADY_EXISTS: t('이미 등록된 이메일입니다.'),
    RECOVERY_EMAIL_ALREADY_EXISTS: t('이미 등록된 이메일입니다.'),
    REFERRAL_NOT_FOUND: t('추천 코드를 확인할 수 없습니다.'),
    AGENT_NOT_FOUND: t('추천 코드를 확인할 수 없습니다.'),
    REFERRAL_INACTIVE: t('현재 사용할 수 없는 추천 코드입니다.'),
    AGENT_INACTIVE: t('현재 사용할 수 없는 추천 코드입니다.'),
    SIGNUP_DISABLED: t('현재 회원가입이 중지되어 있습니다.'),
    RATE_LIMITED: t('요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.'),
  };

  return (
    data?.message ||
    knownMessages[rawCode] ||
    (Array.isArray(data?.errors) ? data.errors.map((item) => item?.message || item).join('\n') : '') ||
    error?.message ||
    t('회원가입 요청을 처리하지 못했습니다.')
  );
}

function buildSignupPayload(form, agreements) {
  const id = form.id.trim();
  const nickname = form.nickname.trim();
  const recoveryEmail = form.email.trim().toLowerCase();
  const referralCode = form.referralCode.trim();

  return {
    id,
    nickname,
    password: form.password,

    // shared/User_DB의 실제 컬럼명
    recovery_email: recoveryEmail,
    marketing_email_consent: Boolean(agreements.marketing),
    marketing_email_consent_version: MARKETING_CONSENT_VERSION,

    // 기존/다른 버전의 어드민 API와의 호환 별칭
    email: recoveryEmail,
    marketing_consent: Boolean(agreements.marketing),

    // agent_node.line_code 또는 기존 referral_code 처리 모두 대응
    referral_code: referralCode || undefined,
    agent_line_code: referralCode || undefined,
    line_code: referralCode || undefined,

    terms_consent: true,
    privacy_consent: true,
  };
}

async function requestSignup(payload) {
  const endpoints = CONFIGURED_SIGNUP_ENDPOINT
    ? [CONFIGURED_SIGNUP_ENDPOINT]
    : DEFAULT_SIGNUP_ENDPOINTS;
  let lastError;

  for (let index = 0; index < endpoints.length; index += 1) {
    try {
      const response = await api.post(endpoints[index], payload);
      if (response?.data?.ok === false || response?.data?.success === false) {
        const responseError = new Error(response?.data?.message || t('회원가입 요청이 거절되었습니다.'));
        responseError.response = response;
        throw responseError;
      }
      return response;
    } catch (error) {
      lastError = error;
      const status = Number(error?.response?.status || 0);
      const canTryFallback = !CONFIGURED_SIGNUP_ENDPOINT && (status === 404 || status === 405);
      if (!canTryFallback || index === endpoints.length - 1) throw error;
    }
  }

  throw lastError || new Error(t('회원가입 API를 찾지 못했습니다.'));
}

export default function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialReferralCode = useMemo(
    () =>
      searchParams.get('referral') ||
      searchParams.get('ref') ||
      searchParams.get('code') ||
      searchParams.get('agent') ||
      searchParams.get('line') ||
      searchParams.get('line_code') ||
      '',
    [searchParams]
  );

  const [form, setForm] = useState({
    id: '',
    nickname: '',
    email: '',
    password: '',
    passwordConfirm: '',
    referralCode: initialReferralCode,
  });
  const [agreements, setAgreements] = useState({
    terms: false,
    privacy: false,
    marketing: false,
  });
  const [openDetail, setOpenDetail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [completed, setCompleted] = useState(false);

  const requiredAgreed = agreements.terms && agreements.privacy;
  const allAgreed = requiredAgreed && agreements.marketing;

  const updateForm = (key, value) => {
    setForm((previous) => ({ ...previous, [key]: value }));
    if (error) setError('');
  };

  const updateAgreement = (key, checked) => {
    setAgreements((previous) => ({ ...previous, [key]: checked }));
    if (error) setError('');
  };

  const toggleAll = (checked) => {
    setAgreements({ terms: checked, privacy: checked, marketing: checked });
    if (error) setError('');
  };

  const validate = () => {
    const id = form.id.trim();
    const nickname = form.nickname.trim();
    const email = form.email.trim();

    if (!/^[A-Za-z0-9_]{4,20}$/.test(id)) {
      return t('아이디는 영문, 숫자, 밑줄을 사용해 4~20자로 입력해 주세요.');
    }
    if (nickname.length < 2 || nickname.length > 16) {
      return t('닉네임은 2~16자로 입력해 주세요.');
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      return t('사용 가능한 이메일 주소를 입력해 주세요.');
    }
    if (form.password.length < 8) {
      return t('비밀번호는 8자 이상 입력해 주세요.');
    }
    if (form.password !== form.passwordConfirm) {
      return t('비밀번호 확인 값이 일치하지 않습니다.');
    }
    if (!requiredAgreed) {
      return t('이용약관과 개인정보 수집 및 이용에 동의해 주세요.');
    }
    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading || completed) return;

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      await requestSignup(buildSignupPayload(form, agreements));
      setCompleted(true);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-page">
      <section className="signup-shell" aria-labelledby="signup-title">
        <aside className="signup-intro" aria-label={t('회원가입 안내')}>
          <div className="signup-brand-row">
            <BrandLogo />
            <div className="signup-brand-copy">
              <strong>ADMIN NETWORK</strong>
              <span>ACCOUNT CENTER</span>
            </div>
          </div>

          <div className="signup-intro-copy">
            <p className="signup-eyebrow">CREATE YOUR ACCOUNT</p>
            <h1>
              {t('관리 서비스를 위한')}
              <br />
              {t('새 계정을 생성합니다.')}
            </h1>
            <p>
              {t('필수 정보를 입력하고 개인정보 수집 및 이용 안내를 확인한 뒤 가입을 완료해 주세요.')}
            </p>
          </div>

          <ol className="signup-intro-points">
            <li>
              <span>01</span>
              <div>
                <strong>{t('정보 입력')}</strong>
                <small>{t('계정과 복구 정보를 정확히 입력합니다.')}</small>
              </div>
            </li>
            <li>
              <span>02</span>
              <div>
                <strong>{t('약관 동의')}</strong>
                <small>{t('필수 약관과 개인정보 안내를 확인합니다.')}</small>
              </div>
            </li>
            <li>
              <span>03</span>
              <div>
                <strong>{t('관리 도구 접속')}</strong>
                <small>{t('접속을 시작합니다.')}</small>
              </div>
            </li>
          </ol>

          <div className="signup-security-note">
            <i aria-hidden="true" />
            <p>
              <strong>{t('보안 안내')}</strong> {t('비밀번호는 다른 서비스와 다르게 설정해 주세요.')}
            </p>
          </div>

          <p className="signup-copyright">© 2026 NIGHT Poker Business</p>
        </aside>

        <section className="signup-card">
          <div className="signup-toolbar">
            <Link className="signup-back-link" to="/login" aria-label={t('로그인 화면으로 돌아가기')}>
              <span aria-hidden="true">←</span>
              {t('로그인')}
            </Link>
            <LanguageSelect className="auth-language-select" />
          </div>

          <div className="signup-content">
            <header className="signup-form-header">
              <span className="signup-form-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M15 19a6 6 0 0 0-12 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  <circle cx="9" cy="8" r="4" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M18 8v6M15 11h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </span>
              <div>
                <p>CREATE ACCOUNT</p>
                <h2 id="signup-title">{t('회원가입')}</h2>
                <span>{t('서비스 이용에 필요한 기본 정보를 입력해 주세요.')}</span>
              </div>
            </header>

            <section className="signup-privacy-notice" aria-labelledby="privacy-title">
              <div className="signup-notice-heading">
                <span className="signup-notice-icon" aria-hidden="true">!</span>
                <div>
                  <p>PRIVACY NOTICE</p>
                  <h3 id="privacy-title">{t('개인정보 수집 및 이용 안내')}</h3>
                </div>
                <span className="signup-required-badge">{t('필수 확인')}</span>
              </div>

              <p className="signup-notice-summary">
                {t('회원가입 및 계정 관리를 위해 아래 개인정보를 수집합니다. 내용을 확인한 후 가입을 진행해 주세요.')}
              </p>

              <dl className="signup-notice-grid">
                <div>
                  <dt><i className="is-gray" />{t('수집 항목')}</dt>
                  <dd>{t('아이디, 닉네임, 이메일, 비밀번호')}</dd>
                </div>
                <div>
                  <dt><i className="is-green" />{t('이용 목적')}</dt>
                  <dd>{t('회원 식별, 계정 관리, 본인 확인 및 계정 복구')}</dd>
                </div>
                <div>
                  <dt><i className="is-red" />{t('보유 기간')}</dt>
                  <dd>{t('회원 탈퇴 시까지 또는 관계 법령에서 정한 기간')}</dd>
                </div>
              </dl>

              <div className="signup-notice-footer">
                <p>{t('필수 개인정보 수집에 동의하지 않으면 회원가입이 제한됩니다.')}</p>
                <button type="button" onClick={() => setOpenDetail(openDetail === 'privacy' ? '' : 'privacy')}>
                  {t('개인정보 안내')} {openDetail === 'privacy' ? t('닫기') : t('보기')}
                </button>
              </div>

              {openDetail === 'privacy' && (
                <div className="signup-detail-panel">
                  {t('회원 식별과 계정 관리·복구를 위해 아이디, 닉네임, 이메일 및 인증 정보를 처리하며, 이용 목적 달성 후 관련 법령에 따라 보관 또는 파기합니다.')}
                </div>
              )}
            </section>

            {completed ? (
              <section className="signup-complete" aria-live="polite">
                <span aria-hidden="true">✓</span>
                <h3>{t('성공')}</h3>
                <p>{t('회원가입이 완료되었습니다. 바로 로그인할 수 있습니다.')}</p>
                <button type="button" onClick={() => navigate('/login', { replace: true, state: { registeredId: form.id.trim() } })}>
                  {t('로그인 화면으로 이동')} <b aria-hidden="true">→</b>
                </button>
              </section>
            ) : (
              <form className="signup-form" onSubmit={handleSubmit} noValidate>
                <div className="signup-form-grid is-two-columns">
                  <label className="signup-field">
                    <span className="signup-field-label">{t('아이디')} <strong>*</strong></span>
                    <input
                      type="text"
                      autoComplete="username"
                      placeholder={t('영문·숫자 4~20자')}
                      maxLength={20}
                      value={form.id}
                      onChange={(event) => updateForm('id', event.target.value)}
                      disabled={loading}
                      autoFocus
                    />
                    <small>{t('로그인에 사용할 아이디입니다.')}</small>
                  </label>

                  <label className="signup-field">
                    <span className="signup-field-label">{t('닉네임')} <strong>*</strong></span>
                    <input
                      type="text"
                      autoComplete="nickname"
                      placeholder={t('표시할 닉네임')}
                      maxLength={16}
                      value={form.nickname}
                      onChange={(event) => updateForm('nickname', event.target.value)}
                      disabled={loading}
                    />
                    <small>{t('관리 화면에 표시되는 이름입니다.')}</small>
                  </label>
                </div>

                <label className="signup-field">
                  <span className="signup-field-label">{t('이메일')} <strong>*</strong></span>
                  <input
                    type="email"
                    autoComplete="email"
                    placeholder="name@example.com"
                    value={form.email}
                    onChange={(event) => updateForm('email', event.target.value)}
                    disabled={loading}
                  />
                  <small>{t('계정 확인 및 복구에 사용됩니다.')}</small>
                </label>

                <div className="signup-form-grid is-two-columns">
                  <label className="signup-field">
                    <span className="signup-field-label">{t('비밀번호')} <strong>*</strong></span>
                    <input
                      type="password"
                      autoComplete="new-password"
                      placeholder={t('8자 이상 입력')}
                      value={form.password}
                      onChange={(event) => updateForm('password', event.target.value)}
                      disabled={loading}
                    />
                    <small>{t('영문, 숫자, 특수문자 조합을 권장합니다.')}</small>
                  </label>

                  <label className="signup-field">
                    <span className="signup-field-label">{t('비밀번호 확인')} <strong>*</strong></span>
                    <input
                      type="password"
                      autoComplete="new-password"
                      placeholder={t('비밀번호 다시 입력')}
                      value={form.passwordConfirm}
                      onChange={(event) => updateForm('passwordConfirm', event.target.value)}
                      disabled={loading}
                    />
                    <small>{t('동일한 비밀번호를 입력해 주세요.')}</small>
                  </label>
                </div>

                <label className="signup-field">
                  <span className="signup-field-label">{t('추천 코드')} <em>{t('선택')}</em></span>
                  <span className="signup-input-with-badge">
                    <input
                      type="text"
                      autoComplete="off"
                      placeholder={t('추천 코드 또는 에이전트 라인 코드')}
                      maxLength={64}
                      value={form.referralCode}
                      onChange={(event) => updateForm('referralCode', event.target.value)}
                      disabled={loading}
                    />
                    <b>OPTIONAL</b>
                  </span>
                </label>

                <fieldset className="signup-agreement-box">
                  <legend className="sr-only">{t('이용 약관 동의')}</legend>

                  <label className="signup-check-row is-all">
                    <input type="checkbox" checked={allAgreed} onChange={(event) => toggleAll(event.target.checked)} disabled={loading} />
                    <span className="signup-check-ui" aria-hidden="true" />
                    <span className="signup-check-copy">
                      <strong>{t('전체 동의')}</strong>
                      <small>{t('필수 및 선택 항목에 모두 동의합니다.')}</small>
                    </span>
                  </label>

                  <div className="signup-agreement-divider" />

                  <label className="signup-check-row">
                    <input type="checkbox" checked={agreements.terms} onChange={(event) => updateAgreement('terms', event.target.checked)} disabled={loading} />
                    <span className="signup-check-ui" aria-hidden="true" />
                    <span className="signup-check-copy"><strong><b className="is-required">{t('[필수]')}</b> {t('이용약관 동의')}</strong></span>
                    <button type="button" onClick={() => setOpenDetail(openDetail === 'terms' ? '' : 'terms')}>{t('보기')}</button>
                  </label>

                  {openDetail === 'terms' && (
                    <div className="signup-detail-panel is-agreement">
                      {t('계정은 본인 또는 정당한 권한을 가진 사용자가 생성해야 하며, 계정 공유·권한 오용·서비스 운영 방해 행위는 제한될 수 있습니다.')}
                    </div>
                  )}

                  <label className="signup-check-row">
                    <input type="checkbox" checked={agreements.privacy} onChange={(event) => updateAgreement('privacy', event.target.checked)} disabled={loading} />
                    <span className="signup-check-ui" aria-hidden="true" />
                    <span className="signup-check-copy"><strong><b className="is-required">{t('[필수]')}</b> {t('개인정보 수집 및 이용 동의')}</strong></span>
                    <button type="button" onClick={() => setOpenDetail(openDetail === 'privacy-agreement' ? '' : 'privacy-agreement')}>{t('보기')}</button>
                  </label>

                  {openDetail === 'privacy-agreement' && (
                    <div className="signup-detail-panel is-agreement">
                      {t('회원 식별과 계정 관리·복구를 위해 아이디, 닉네임, 이메일 및 인증 정보를 처리하며, 이용 목적 달성 후 관련 법령에 따라 보관 또는 파기합니다.')}
                    </div>
                  )}

                  <label className="signup-check-row">
                    <input type="checkbox" checked={agreements.marketing} onChange={(event) => updateAgreement('marketing', event.target.checked)} disabled={loading} />
                    <span className="signup-check-ui" aria-hidden="true" />
                    <span className="signup-check-copy"><strong><b className="is-optional">{t('[선택]')}</b> {t('이메일 마케팅 정보 수신')}</strong></span>
                  </label>
                </fieldset>

                {error && <div className="signup-error" role="alert">{error}</div>}

                <button className="signup-submit-button" type="submit" disabled={loading || !requiredAgreed}>
                  {loading ? t('회원가입 처리 중…') : t('회원가입')}
                  <span aria-hidden="true">→</span>
                </button>

                <p className="signup-login-prompt">
                  {t('이미 계정이 있으신가요?')} <Link to="/login">{t('로그인')}</Link>
                </p>
              </form>
            )}
          </div>
        </section>
      </section>
    </div>
  );
}
